import { Capacitor, registerPlugin } from "@capacitor/core";

/**
 * The shell intentionally exposes only App, Share, and a two-method cache file bridge. This
 * adapter is the single place Console may cross that boundary; browsers retain their existing path.
 */
export const MAX_NATIVE_SHARE_BYTES = 50 * 1024 * 1024;
export const REMOTE_FILE_FETCH_TIMEOUT_MS = 30_000;

export type FileDeliveryResult =
  | { kind: "native-share" }
  | { kind: "browser-download" }
  | { kind: "browser-open" };

export interface NativeFileBridge {
  writeCacheFile(path: string, base64Data: string): Promise<{ uri: string }>;
  shareFile(options: { fileUri: string; title: string }): Promise<void>;
  deleteCacheFile(path: string): Promise<void>;
}

interface SecOpsFileBridgePlugin {
  writeCacheFile(options: { path: string; data: string }): Promise<{ uri: string }>;
  deleteCacheFile(options: { path: string }): Promise<void>;
}

const SecOpsFileBridge = registerPlugin<SecOpsFileBridgePlugin>("SecOpsFileBridge");

export interface FileDeliveryRuntime {
  /** Test seam. Production derives this from Capacitor and permits the bridge only on iOS. */
  nativeIos?: boolean;
  /** Test seam. Production dynamically loads the registered plugins only when needed. */
  nativeBridge?: NativeFileBridge;
  document?: Document;
  openWindow?: (url: string) => Window | null;
  fetchFile?: typeof fetch;
  /** Test seam. Production bounds signed-evidence fetch + stream consumption to 30 seconds. */
  fetchTimeoutMs?: number;
  now?: () => number;
}

export interface BlobFileDelivery {
  blob: Blob;
  fileName: string;
  title: string;
}

export interface RemoteFileDelivery {
  url: string;
  fileName: string;
  title: string;
}

/** A value-safe error. Never attach a signed URL, native path, or backend response body. */
export class FileDeliveryError extends Error {
  constructor(message = "The file could not be prepared for sharing.") {
    super(message);
    this.name = "FileDeliveryError";
  }
}

function isNativeIos(runtime: FileDeliveryRuntime): boolean {
  return (
    runtime.nativeIos ??
    (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios")
  );
}

/** Reduce an operator-visible display name to one cache-safe basename (no path traversal). */
export function safeFileName(requested: string): string {
  // Normalize before locating the basename: compatibility characters such as the fullwidth
  // solidus normalize into real path separators and must not survive the traversal boundary.
  const basename = requested.normalize("NFKC").trim().split(/[\\/]/).at(-1) ?? "";
  const normalized = basename
    .replace(/[\u0000-\u001f\u007f<>:"|?*]/g, "-")
    .replace(/\s+/g, " ")
    .replace(/^[. ]+|[. ]+$/g, "")
    .slice(0, 120)
    .replace(/[. ]+$/g, "");
  return normalized.length > 0 && normalized !== "." && normalized !== ".."
    ? normalized
    : "secops-export";
}

async function readBoundedResponseBlob(response: Response): Promise<Blob> {
  const type = response.headers.get("content-type") ?? "";
  const reader = response.body?.getReader();
  if (!reader) {
    // Without a stream there is no way to enforce the byte bound before buffering. Fail closed.
    throw new FileDeliveryError();
  }

  const chunks: ArrayBuffer[] = [];
  let receivedBytes = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    receivedBytes += value.byteLength;
    if (receivedBytes > MAX_NATIVE_SHARE_BYTES) {
      void reader.cancel().catch(() => undefined);
      throw new FileDeliveryError("This file is too large to share from the iOS shell.");
    }
    const copy = new Uint8Array(value.byteLength);
    copy.set(value);
    chunks.push(copy.buffer);
  }
  return new Blob(chunks, { type });
}

function createObjectUrlDownload(blob: Blob, fileName: string, doc: Document): void {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = doc.createElement("a");
    anchor.href = url;
    anchor.download = safeFileName(fileName);
    anchor.rel = "noopener";
    doc.body.appendChild(anchor);
    anchor.click();
    doc.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function openBrowserFile(url: string, runtime: FileDeliveryRuntime): void {
  const doc = runtime.document ?? document;
  const openWindow = runtime.openWindow ?? ((href: string) => window.open(href, "_blank", "noopener,noreferrer"));
  if (openWindow(url) !== null) return;

  // Popup heuristics sometimes return null even after the signer succeeds. Preserve the existing
  // explicit anchor fallback, but never render or log the short-lived URL.
  const anchor = doc.createElement("a");
  anchor.href = url;
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  anchor.click();
}

function blobAsBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new FileDeliveryError());
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new FileDeliveryError());
        return;
      }
      const comma = result.indexOf(",");
      if (comma < 0) {
        reject(new FileDeliveryError());
        return;
      }
      resolve(result.slice(comma + 1));
    };
    reader.readAsDataURL(blob);
  });
}

async function loadNativeBridge(): Promise<NativeFileBridge> {
  const { Share } = await import("@capacitor/share");
  return {
    writeCacheFile: (path, data) =>
      SecOpsFileBridge.writeCacheFile({ path, data }),
    shareFile: async ({ fileUri, title }) => {
      await Share.share({ title, files: [fileUri] });
    },
    deleteCacheFile: (path) => SecOpsFileBridge.deleteCacheFile({ path }),
  };
}

async function shareNativeBlob(
  { blob, fileName, title }: BlobFileDelivery,
  runtime: FileDeliveryRuntime,
): Promise<FileDeliveryResult> {
  if (blob.size > MAX_NATIVE_SHARE_BYTES) {
    throw new FileDeliveryError("This file is too large to share from the iOS shell.");
  }

  const bridge = runtime.nativeBridge ?? (await loadNativeBridge());
  const timestamp = (runtime.now ?? Date.now)();
  const nonce = globalThis.crypto?.randomUUID?.().slice(0, 8) ?? "export";
  const cachePath = `${timestamp}-${nonce}-${safeFileName(fileName)}`;
  const data = await blobAsBase64(blob);
  const written = await bridge.writeCacheFile(cachePath, data);
  try {
    await bridge.shareFile({ fileUri: written.uri, title });
  } finally {
    // The file exists only to back the share sheet. Do not leave incident evidence or reports in
    // app cache after the sheet closes; cleanup failure must not turn a completed share into error.
    await bridge.deleteCacheFile(cachePath).catch(() => undefined);
  }
  return { kind: "native-share" };
}

export async function deliverBlobFile(
  delivery: BlobFileDelivery,
  runtime: FileDeliveryRuntime = {},
): Promise<FileDeliveryResult> {
  if (isNativeIos(runtime)) {
    return shareNativeBlob(delivery, runtime);
  }
  createObjectUrlDownload(
    delivery.blob,
    delivery.fileName,
    runtime.document ?? document,
  );
  return { kind: "browser-download" };
}

export async function deliverRemoteFile(
  delivery: RemoteFileDelivery,
  runtime: FileDeliveryRuntime = {},
): Promise<FileDeliveryResult> {
  let parsed: URL;
  try {
    parsed = new URL(delivery.url);
  } catch {
    throw new FileDeliveryError();
  }
  if (parsed.protocol !== "https:") {
    throw new FileDeliveryError();
  }

  if (!isNativeIos(runtime)) {
    openBrowserFile(parsed.toString(), runtime);
    return { kind: "browser-open" };
  }

  const controller = new AbortController();
  const timeout = globalThis.setTimeout(
    () => controller.abort(),
    runtime.fetchTimeoutMs ?? REMOTE_FILE_FETCH_TIMEOUT_MS,
  );
  try {
    const response = await (runtime.fetchFile ?? fetch)(parsed.toString(), {
      cache: "no-store",
      credentials: "omit",
      redirect: "error",
      referrerPolicy: "no-referrer",
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new FileDeliveryError();
    }

    const declaredSize = Number(response.headers.get("content-length"));
    if (Number.isFinite(declaredSize) && declaredSize > MAX_NATIVE_SHARE_BYTES) {
      throw new FileDeliveryError("This file is too large to share from the iOS shell.");
    }
    return shareNativeBlob(
      {
        // Do not trust Content-Length alone: signed/chunked responses can omit or misreport it.
        // Bound the stream before it can be fully buffered into the WKWebView process.
        blob: await readBoundedResponseBlob(response),
        fileName: delivery.fileName,
        title: delivery.title,
      },
      runtime,
    );
  } catch (error) {
    if (error instanceof FileDeliveryError) throw error;
    throw new FileDeliveryError();
  } finally {
    globalThis.clearTimeout(timeout);
  }
}
